// 앱 공용 실시간 소켓(알림 버스).
// 과거 modules/remote/services/ChatService.js 였으나, Remote 채팅 전용에서
// 앱 전역(로그인~로그아웃) 알림 채널로 승격되며 Core 로 이전됨.
// - 연결: 로그인 성공/부팅(토큰 존재) 시 ensureChatConnected
// - 해제: 로그아웃 시 chatDisConnect
// - 수신: chatOnMessage(eventName, handler) / 해제: chatOffMessage(eventName, handler)
//   (전역 소켓이라 화면이 unmounted 에서 반드시 off 해야 핸들러가 누적되지 않음)
import { io } from "socket.io-client";
import { CustomAlert } from "@/assets/js/util";
import i18n from "@/i18n";
import { BASE_PREFIX } from "@/config";

// 채팅 소켓 서버 URL — 진입점마다 중복 하드코딩하지 않도록 한 곳에서 관리.
// prod(Nginx 동일도메인): VUE_APP_CHAT_URL 을 비워두면 same-origin(현재 접속 origin)으로 연결
//   → Nginx 가 /socket.io → chat:8733 로 프록시(WS upgrade). dev: 아래 기본값(별도 포트).
export const CHAT_SOCKET_URL =
  process.env.VUE_APP_CHAT_URL !== undefined &&
  process.env.VUE_APP_CHAT_URL !== ""
    ? process.env.VUE_APP_CHAT_URL
    : process.env.NODE_ENV === "production"
    ? window.location.origin
    : "https://dev.mdtdexter.com:8733";

let chatSocket = null;
let chatRoomInfo = {
  nickname: "",
  id: "",

  room: {
    roomId: "aabbcc-id",
    roomName: "aabbcc",
    screenShareTarget: "",
    screenDocTarget: "",
  },
};

/**
 * Connect to Socket Server
 * @param {String} url
 */
export function chatConnect(url, userId) {
  // 재연결 시 기존 소켓이 좀비로 남지 않도록 먼저 정리
  if (chatSocket) {
    chatSocket.disconnect();
    chatSocket = null;
  }

  // 핸드셰이크에 access_token 전달 → 서버가 서명검증 후 session_id 추출(위조 불가).
  // auth/query 둘 다 실어 socket.io 버전 차이에 대비.
  let token = "";
  try {
    token = localStorage.getItem("accessToken") || "";
  } catch (e) {
    token = "";
  }

  chatSocket = io(url, {
    // 서브경로(/rmt) 배포 시 socket.io 경로도 /rmt/socket.io 로. 루트면 기본 /socket.io.
    path: (BASE_PREFIX || "") + "/socket.io",
    transports: ["websocket"],
    reconnectionAttempts: 5,
    auth: { token },
    query: { token },
  });

  // 다른 기기/브라우저에서 같은 계정으로 로그인(또는 글래스 QR)하면 서버가 이 세션을 종료 신호로 푸시.
  // → 알림을 띄우고, 확인 누르면 로컬 로그아웃(토큰 삭제 + /login). (중복 실행 방지 플래그)
  // [다중 로그인 허용 — 2026-10-16] 다른 기기 로그인 시 강제 로그아웃 비활성화.
  //   서버(Auth)가 revoke 안 하도록 바뀌었고, 웹도 세션종료 push 를 무시하도록 아래 리스너 주석처리.
  //   원복하려면 sessionCloseHandled 선언과 chatSocket.on(...) 블록 주석 해제.
  // let sessionCloseHandled = false;
  // chatSocket.on("REMOTE_REQ_SESSION_CLOSE", () => {
  //   if (sessionCloseHandled) return;
  //   sessionCloseHandled = true;
  //   const doLogout = () => {
  //     try {
  //       localStorage.removeItem("accessToken");
  //       localStorage.removeItem("refreshToken");
  //     } catch (e) {
  //       /* 정리 실패 무시 */
  //     }
  //     try {
  //       chatSocket && chatSocket.disconnect();
  //     } catch (e) {
  //       /* 무시 */
  //     }
  //     chatSocket = null;
  //     if (window.location.pathname !== "/login") {
  //       window.location.href = "/login";
  //     }
  //   };
  //   // 앱 공용 알림(확인 버튼 1개) → 확인 시 로그아웃
  //   CustomAlert(
  //     i18n.global.t("login.sessionClosed"),
  //     false,
  //     doLogout,
  //     doLogout,
  //   );
  // });

  chatSocket.on("connect", () => {
    console.log("##Connected to Socket.IO server : " + userId);

    let tempNickName = userId;
    chatRoomInfo.nickname = tempNickName;
    chatRoomInfo.id = tempNickName;

    // set init
    const nickname = chatRoomInfo.nickname;
    chatSocket.emit("setInit", { nickname }, (response) => {
      chatRoomInfo.nickname = response.nickname;
      chatRoomInfo.id = chatSocket.id;
      chatRoomInfo.room = response.room;
    });
  });

  chatSocket.on("disconnect", () => {
    console.log("##Disconnected from Socket.IO server");
  });

  chatSocket.on("connect_error", (err) => {
    console.error("##Connection error:", err);
  });
}

/**
 * 소켓 연결 보장(멱등). 이미 소켓이 있으면(연결/재연결 시도 중 포함) 재사용하고,
 * 없을 때만 새로 연결한다. 앱 전역에서 로그인 후 여러 번 호출해도 안전.
 * ※ 전역 소켓은 "한 번 연결 후 유지"가 원칙 — chatConnect 재호출로 인스턴스를
 *    갈아치우면 화면들이 등록한 .on 핸들러가 유실되므로, 항상 ensure 로만 연결할 것.
 * @param {String} userId
 * @param {String} [url] 기본값 CHAT_SOCKET_URL
 */
export function ensureChatConnected(userId, url = CHAT_SOCKET_URL) {
  if (chatSocket) return; // 이미 연결(또는 재연결 시도) 중이면 그대로 사용
  chatConnect(url, userId);
}

/**
 * generate new room
 * @param {*} roomId
 */
export function createChatRoom(domObj, roomId) {
  chatSocket.emit("createChatRoom", roomId, (res) => {
    enterChatRoom(domObj, roomId);

    console.log("## createChatRoom : " + JSON.stringify(res));
  });
}

/**
 * entered Chat room
 * @param {*} roomId
 */
export function enterChatRoom(domObj, roomId) {
  chatSocket.emit("enterChatRoom", roomId, (res) => {
    if (!res) return;

    console.log("## enterChatRoom : " + JSON.stringify(res));

    chatRoomInfo.room = res;
    domObj.screenShareTarget = chatRoomInfo.room.screenShareTarget;
    domObj.roomScreenShare = chatRoomInfo.room.screenDocTarget;
  });
}

/**
 * 실시간 접속상태(presence) 조회 — 소켓 서버가 라이브 소켓 기준으로 { UserId: 'Online'|'Remote' } 반환.
 * (없는 UserId = Offline). 소켓 미연결이면 빈 객체.
 * @returns {Promise<Object>}
 */
export function chatGetOnlineUsers() {
  return new Promise((resolve) => {
    if (!chatSocket) {
      resolve({});
      return;
    }
    try {
      chatSocket.emit("getOnlineUsers", (res) => resolve(res || {}));
    } catch (e) {
      resolve({});
    }
  });
}

/**
 * Zone 명시적 이탈 — 방에서 나가 서버가 빈 방/화면공유를 정리하게 한다.
 * (전역 소켓이라 Zone 을 나가도 소켓이 안 끊기므로, 방 정리를 명시적으로 알려야 함)
 */
export function chatLeaveChatRoom() {
  if (chatSocket) {
    chatSocket.emit("leaveChatRoom");
  }
}

/**
 * Disconnect to Socket Server
 */
export function chatDisConnect() {
  if (chatSocket) {
    chatSocket.disconnect();
    chatSocket = null;
  }
}

/**
 * Receive message from Socket server
 * ※ 전역 소켓이라 등록한 핸들러는 소켓이 살아있는 동안 유지된다.
 *    화면이 unmounted 될 때 반드시 chatOffMessage 로 같은 handler 를 해제할 것(누적 방지).
 * @param {String} eventName
 * @param {Function} callback
 */
export function chatOnMessage(eventName, callback) {
  if (chatSocket) {
    chatSocket.on(eventName, callback);
  } else {
    console.warn("##Socket is not initialized");
  }
}

/**
 * Stop receiving message from Socket server
 * @param {String} eventName
 * @param {Function} [callback] 생략 시 해당 이벤트의 모든 핸들러 제거
 */
export function chatOffMessage(eventName, callback) {
  if (!chatSocket) return;
  if (callback) {
    chatSocket.off(eventName, callback);
  } else {
    chatSocket.off(eventName);
  }
}

/**
 * Send message to Socket server
 * @param {String} pSendMsg
 */
function chatSendMessageCommon(pEventName, pSendMsg) {
  if (chatSocket) {
    chatSocket.emit(pEventName, pSendMsg);
  } else {
    console.warn("##Socket is not connected");
  }
}

/**
 * SendMessage - Chat
 * @param {*} pSendMsg
 */
export function chatSendMessage(pSendMsg) {
  let data = {
    type: "CHAT",
    msg: pSendMsg,
  };

  chatSendMessageCommon("sendMessage", data);
}

/**
 * SendMessage - MainScreen Changed
 * @param {*} pSendData
 */
export function chatSendMessageMainScreenChange(pSendData) {
  let data = {
    type: "SCREEN_SHARE_TARGTE",
    msg: pSendData,
  };

  chatSendMessageCommon("sendMessageScreenShareTarget", data);
}

/**
 * SendMessage - Send files
 * @param {*} pSendData
 */
export function chatSendMessageFile(pSendData) {
  let data = {
    type: "FILE",
    msg: pSendData,
  };

  chatSendMessageCommon("sendMessage", data);
}

/**
 * SendMessage - ArDrawing
 * @param {*} pSendData
 */
export function chatSendMessageArDrawing(pSendData) {
  let data = {
    type: "AR-DRAWIMG",
    msg: pSendData,
  };

  chatSendMessageCommon("sendMessage", data);
}

/**
 * SendMessage - Room deleted
 * @param {*} pSendData
 */
export function chatSendMessageRoomDeleteLeader(pSendData) {
  let data = {
    type: "ROOM-DELETE-LEADER",
    msg: pSendData,
  };

  chatSendMessageCommon("sendMessage", data);
}

/**
 * SendMessage - Room changed leader
 * @param {*} pSendData
 */
export function chatSendMessageRoomChangedLeader(pSendData) {
  let data = {
    type: "ROOM-CHANGE-LEADER",
    msg: pSendData,
  };

  chatSendMessageCommon("sendMessage", data);

  data.type = "CHAT";
  data.msg = pSendData + "님이 Leader로 변경되었습니다.";
  chatSendMessageCommon("sendMessage", data);
}

/**
 * SendMessage - Invite to user on lobby
 * @param {*} pSendData
 */
export function chatSendMessageInvite(pSendData) {
  let data = {
    type: "INVITE",
    msg: pSendData,
  };

  chatSendMessageCommon("sendMessagePrivate", data);
}

/**
 * 원격지원 요청 응답(승인/거절) 전송.
 * 게이트웨이(@SubscribeMessage 'REMOTE_REQ_ANS')가 msgObj.UserId 로 요청자 소켓을 찾아
 * { UserId: 응답자닉네임, result } 를 그 요청자에게 전달한다.
 * ※ 서버가 읽는 키는 소문자 result (ACC/REJ).
 * @param {String} targetUserId 요청자(모바일) UserId
 * @param {"ACC"|"REJ"} result
 */
export function sendRemoteReqAns(targetUserId, result) {
  chatSendMessageCommon("REMOTE_REQ_ANS", { UserId: targetUserId, result });
}

/**
 * SendMessage - Stream document - start
 * @param {*} pSendData
 */
export function chatSendMessageStreamingDocumentsStart(pSendData) {
  let data = {
    type: "STREAM-DOC-START",
    msg: pSendData,
  };

  chatSendMessageCommon("sendMessageScreenDocTarget", data);
}

/**
 * SendMessage - Stream document - finish
 * @param {*} pSendData
 */
export function chatSendMessageStreamingDocumentsFinish(pSendData) {
  let data = {
    type: "STREAM-DOC-FINISH",
    msg: pSendData,
  };

  chatSendMessageCommon("sendMessageScreenDocTarget", data);
}
